import React, { useState } from "react";
import { displayName } from "@/lib/names";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, X, HelpCircle } from "lucide-react";
import { ContributionDialog } from "@/components/ContributionDialog";

const LABELS = {
  first_name: "Prénom", last_name: "Nom", nickname: "Surnom", gender: "Genre", clan_key: "Famille", birth_year: "Naissance",
  birth_year_approx: "Date approximative", death_year: "Décès", birth_place: "Lieu de naissance", biography: "Biographie",
  father_id: "Père", mother_id: "Mère",
};
const KINDS = { new: "Nouvelle fiche (invisible jusqu'à validation)", edit: "Modification de fiche (version actuelle conservée jusqu'à validation)", source: "Nouvelle source" };
const STATUS = {
  pending: ["En attente", "#A96B18"], info_requested: ["Précisions demandées", "#3E6B8A"],
  accepted: ["Acceptée", "#2C4230"], rejected: ["Refusée", "#8B2A1F"],
};

const ContributionCard = ({ c, byId, clans, allPersons, isOfficer, onChanged }) => {
  const { API, user } = useAuth();
  const [note, setNote] = useState("");
  const [status, color] = STATUS[c.status] || [c.status, "#6E5B4F"];
  const fmt = (k, v) => {
    if (k === "father_id" || k === "mother_id") return byId[v] ? `${displayName(byId[v])}` : v;
    if (k === "clan_key") return clans.find(x => x.key === v)?.name || v;
    if (typeof v === "boolean") return v ? "oui" : "non";
    if (Array.isArray(v)) return v.map(x => (byId[x] ? displayName(byId[x]) : String(x))).join(", ") || "—";
    if (v && typeof v === "object") return JSON.stringify(v);
    return String(v ?? "—");
  };
  const review = async (action) => {
    try {
      await axios.post(`${API}/contributions/${c.id}/review`, { action, note });
      toast.success("Décision enregistrée");
      setNote(""); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const open = c.status === "pending" || c.status === "info_requested";
  const person = byId[c.person_id];

  return (
    <div className="heritage-card p-4" data-testid={`contribution-${c.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link to={`/persons/${c.person_id}`} className="font-serif-heritage text-xl text-[#261911] hover:text-[#993D22]">{c.person_name}</Link>
        <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full border" style={{ color, borderColor: color }} data-testid={`contribution-status-${c.id}`}>{status}</span>
      </div>
      {c.kind && <div className="text-xs font-semibold text-[#993D22]" data-testid={`contribution-kind-${c.id}`}>{KINDS[c.kind] || c.kind}</div>}
      <div className="text-xs text-[#6E5B4F] mb-2">Proposée par {c.proposed_by_name} · {c.created_at?.slice(0, 10)}</div>
      <ul className="text-sm space-y-0.5">
        {Object.entries(c.changes || {}).map(([k, v]) => (
          <li key={k}><span className="text-[#6E5B4F]">{LABELS[k] || k} :</span> <span className="text-[#261911]">{fmt(k, v)}</span></li>
        ))}
      </ul>
      {c.note && <p className="text-sm italic text-[#6E5B4F] mt-2">« {c.note} »</p>}
      {(c.reviews || []).map((r, i) => (
        <p key={i} className="text-xs text-[#3A2312] mt-2 border-l-2 border-[#D9822B] pl-2">{r.by_name} : {r.note || r.action} ({r.at?.slice(0, 10)})</p>
      ))}
      {isOfficer && open && (
        <div className="mt-3 space-y-2">
          <Textarea rows={2} placeholder="Commentaire (obligatoire pour refuser ou demander des précisions)" value={note} onChange={e => setNote(e.target.value)} data-testid={`review-note-${c.id}`} />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="btn-terra" onClick={() => review("accept")} data-testid={`accept-${c.id}`}><Check className="w-4 h-4 mr-1" /> Accepter et intégrer</Button>
            <Button size="sm" variant="outline" className="border-[#3E6B8A] text-[#3E6B8A]" disabled={!note.trim()} onClick={() => review("request_info")} data-testid={`request-info-${c.id}`}><HelpCircle className="w-4 h-4 mr-1" /> Demander des précisions</Button>
            <Button size="sm" variant="outline" className="border-red-700 text-red-700" disabled={!note.trim()} onClick={() => review("reject")} data-testid={`reject-${c.id}`}><X className="w-4 h-4 mr-1" /> Refuser</Button>
          </div>
        </div>
      )}
      {!isOfficer && c.proposed_by === user?.id && open && person && (
        <div className="mt-3">
          <ContributionDialog person={person} allPersons={allPersons} clans={clans} existing={c} onDone={onChanged}
            trigger={(o) => <Button size="sm" variant="outline" onClick={o} data-testid={`edit-contribution-${c.id}`}>Modifier / répondre</Button>} />
        </div>
      )}
    </div>
  );
};

export const ContributionsList = ({ items, allPersons, clans, isOfficer, onChanged }) => {
  const byId = Object.fromEntries(allPersons.map(p => [p.id, p]));
  if (!items.length) return <p className="text-sm italic text-[#6E5B4F]" data-testid="no-contributions">Aucune proposition pour le moment.</p>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {items.map(c => <ContributionCard key={c.id} c={c} byId={byId} clans={clans} allPersons={allPersons} isOfficer={isOfficer} onChanged={onChanged} />)}
    </div>
  );
};

export default ContributionsList;
